import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import Text from 'tio-ui/components/text';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import SectionHeader from 'tio-ui/components/section-header';
import SectionPanel from 'tio-ui/components/section-panel';
import AlertBanner from 'tio-ui/components/alert-banner';
import CtaBanner from 'tio-ui/components/cta-banner';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import {
  PhArrowsMerge,
  PhScales,
  PhThumbsUp,
  PhListChecks,
  PhWarningCircle,
  PhCheck,
  PhX,
  PhArrowsClockwise,
} from 'ember-phosphor-icons';

interface LoanTypeItemSignature {
  Args: {
    translationKey: string;
  };
}

const LoanTypeItem: TOC<LoanTypeItemSignature> = <template>
  <div class="tio-bg-info-soft rounded-lg px-4 py-3 text-sm tio-fg-primary">
    {{t @translationKey}}
  </div>
</template>;

interface ConsolidationSignature {
  Args: {
    onSwitchToRefinancing?: (id: string) => void;
  };
  Element: HTMLElement;
}

export default class RepaymentOptionsConsolidationComponent extends Component<ConsolidationSignature> {
  eligibleLoanTypes = [
    'wellness_insights.subsidized_consolidation_federal_stafford',
    'wellness_insights.unsubsidized_consolidation_federal_stafford',
    'wellness.consolidation_plus_loans_FFEL',
    'wellness.consolidation_supplemental_loans',
    'wellness.consolidation_federal_perkins',
    'wellness.consolidation_nursing_student',
    'wellness.consolidation_nurse_faculty',
    'wellness.consolidation_health_education_assistance',
    'wellness.consolidation_health_professions',
    'wellness.consolidation_loans_for_disadantaged',
    'wellness.consolidation_direct_subsidized_loans',
    'wellness.consolidation_direct_unsubsidized_plus_loans',
    'wellness.consolidation_direct_unsubsidized_loans',
    'wellness.consolidation_FFEL_consolidation',
  ];

  switchToRefinancing = () => {
    this.args.onSwitchToRefinancing?.('refinancing');
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="space-y-8" ...attributes>

      {{! What is Consolidation }}
      <div>
        <SectionHeader
          @icon={{PhArrowsMerge}}
          @title="What is Consolidation?"
          @subtitle="Combine multiple federal loans into one"
          @iconClass="tio-fg-info"
        />
        <Card @padding="lg">
          <Text class="tio-fg-secondary leading-relaxed">
            {{t "wellness.consolidation_description"}}
          </Text>
        </Card>
      </div>

      {{! Consolidation vs Refinancing Comparison }}
      <SectionPanel
        @icon={{PhScales}}
        @title="Consolidation vs Refinancing"
        @iconColor="blue"
        @roundedXl={{true}}
      >
        <:body>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {{! Consolidation column }}
            <Card @padding="md">
              <div class="tio-row-tight mb-4">
                <PhArrowsMerge class="tio-icon-md tio-fg-info" />
                <span class="font-semibold tio-fg-primary">Consolidation</span>
              </div>
              <div class="space-y-3">
                <div class="tio-row-tight">
                  <PhCheck class="tio-icon-md tio-fg-success shrink-0" />
                  <Text @size="sm" class="tio-fg-secondary">{{t
                      "consolidation.combine_loans"
                    }}</Text>
                </div>
                <div class="tio-row-tight">
                  <PhX class="tio-icon-md text-gray-300 shrink-0" />
                  <Text @size="sm" class="tio-fg-disabled">{{t "consolidation.lowers_rate"}}</Text>
                </div>
                <div class="tio-row-tight">
                  <PhCheck class="tio-icon-md tio-fg-success shrink-0" />
                  <Text @size="sm" class="tio-fg-secondary">{{t
                      "consolidation.keeps_loans_eligible"
                    }}</Text>
                </div>
                <div class="tio-row-tight">
                  <PhX class="tio-icon-md text-gray-300 shrink-0" />
                  <Text @size="sm" class="tio-fg-disabled">{{t
                      "consolidation.changes_to_private"
                    }}</Text>
                </div>
              </div>
            </Card>

            {{! Refinancing column }}
            <Card @padding="md">
              <div class="tio-row-tight mb-4">
                <PhArrowsClockwise class="tio-icon-md tio-fg-brand" />
                <span class="font-semibold tio-fg-primary">Refinancing</span>
              </div>
              <div class="space-y-3">
                <div class="tio-row-tight">
                  <PhCheck class="tio-icon-md tio-fg-success shrink-0" />
                  <Text @size="sm" class="tio-fg-secondary">{{t
                      "consolidation.combine_loans"
                    }}</Text>
                </div>
                <div class="tio-row-tight">
                  <PhCheck class="tio-icon-md tio-fg-success shrink-0" />
                  <Text @size="sm" class="tio-fg-secondary">{{t "consolidation.lowers_rate"}}</Text>
                </div>
                <div class="tio-row-tight">
                  <PhX class="tio-icon-md text-gray-300 shrink-0" />
                  <Text @size="sm" class="tio-fg-disabled">{{t
                      "consolidation.keeps_loans_eligible"
                    }}</Text>
                </div>
                <div class="tio-row-tight">
                  <PhCheck class="tio-icon-md tio-fg-success shrink-0" />
                  <Text @size="sm" class="tio-fg-secondary">{{t
                      "consolidation.changes_to_private"
                    }}</Text>
                </div>
              </div>
            </Card>
          </div>
        </:body>
      </SectionPanel>

      {{! Benefits }}
      <SectionPanel
        @icon={{PhThumbsUp}}
        @title="Benefits of Consolidation"
        @iconColor="emerald"
        @roundedXl={{true}}
      >
        <:body>
          <div class="space-y-4">
            <Text class="tio-fg-secondary leading-relaxed">
              {{t "wellness_insights.consolidation_what_benefits_description"}}
            </Text>
            <AlertBanner @intent="tip" @title="Keep in mind">
              {{t "wellness_insights.consolidation_what_benefits_consolidation_not_decrease"}}
            </AlertBanner>
          </div>
        </:body>
      </SectionPanel>

      {{! Risks }}
      <AlertBanner @intent="warning" @title="Risks of Consolidation" @size="lg">
        {{t "wellness.consolidation_risks_description"}}
      </AlertBanner>

      {{! Eligibility }}
      <SectionPanel
        @icon={{PhListChecks}}
        @title="Eligible Loan Types"
        @iconColor="blue"
        @roundedXl={{true}}
      >
        <:body>
          <Text @size="sm" class="tio-fg-secondary mb-6">
            The following federal student loan types are eligible for consolidation:
          </Text>
          <ul
            class="grid grid-cols-1 md:grid-cols-2 gap-4"
            aria-label="Eligible federal student loan types"
          >
            {{#each this.eligibleLoanTypes as |loanType|}}
              <li>
                <LoanTypeItem @translationKey={{loanType}} />
              </li>
            {{/each}}
          </ul>
        </:body>
      </SectionPanel>

      {{! Who Shouldn't Consolidate }}
      <SectionPanel
        @icon={{PhWarningCircle}}
        @title="Who Shouldn't Consolidate?"
        @iconColor="amber"
        @roundedXl={{true}}
      >
        <:body>
          <div class="prose prose-sm dark:prose-invert max-w-none">
            <ol class="list-decimal pl-4 space-y-2 tio-fg-secondary">
              {{t "wellness_insights.consolidation_who_shouldnt_description" htmlSafe=true}}
            </ol>
          </div>
        </:body>
      </SectionPanel>

      {{! Refinancing Alternative CTA }}
      <CtaBanner
        @heading="Refinancing Could Be an Alternative"
        @description="If you have good to excellent credit and a good debt-to-income ratio, refinancing may help you lower your interest rate. Keep in mind that all refinanced loans are private and lose federal borrower protections."
        @intent="info"
      >
        <:action>
          <Button @intent="primary" {{on "click" this.switchToRefinancing}}>
            Explore Refinancing
          </Button>
        </:action>
      </CtaBanner>
    </div>
  </template>
}
